import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Modal, Popconfirm, Row, Space, Table, Tooltip, Typography, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { createBlogCategory, deleteBlogCategory, updateBlogCategory } from '../../api/blog';
import type { BlogCategory, SaveBlogCategoryPayload } from '../../api/blog';
import { ApiError } from '../../api/errors';
import { BrandColors } from '../../theme/brand';

interface CategoryFormValues {
  readonly name: string;
  readonly slug: string | undefined;
  readonly description: string | undefined;
  readonly sortOrder: number | null;
}

interface BlogCategoryModalProps {
  readonly open: boolean;
  readonly categories: readonly BlogCategory[];
  readonly canManage: boolean;
  readonly onClose: () => void;
  /** Gọi sau mỗi lần thêm, sửa, xóa — trang cha tải lại danh sách danh mục. */
  readonly onChanged: () => void;
}

/**
 * Cửa sổ quản lý danh mục blog: một biểu mẫu nhỏ ở trên để thêm hoặc sửa, danh sách ở dưới.
 *
 * Danh mục đang có bài không xóa được — máy chủ chặn, và nút xóa ở đây tắt sẵn kèm lời giải thích.
 */
export function BlogCategoryModal({
  open,
  categories,
  canManage,
  onClose,
  onChanged,
}: BlogCategoryModalProps): ReactNode {
  const [form] = Form.useForm<CategoryFormValues>();
  const [editing, setEditing] = useState<BlogCategory | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function startEdit(category: BlogCategory): void {
    setEditing(category);
    form.setFieldsValue({
      name: category.name,
      slug: category.slug,
      description: category.description ?? undefined,
      sortOrder: category.sortOrder,
    });
  }

  function resetForm(): void {
    setEditing(null);
    form.resetFields();
  }

  async function submit(values: CategoryFormValues): Promise<void> {
    const payload: SaveBlogCategoryPayload = {
      name: values.name,
      slug: values.slug?.trim() === '' ? null : (values.slug ?? null),
      description: values.description?.trim() === '' ? null : (values.description ?? null),
      sortOrder: values.sortOrder ?? 0,
    };

    setIsSaving(true);

    try {
      if (editing === null) {
        await createBlogCategory(payload);
        message.success('Đã thêm danh mục.');
      } else {
        await updateBlogCategory(editing.id, payload);
        message.success('Đã lưu danh mục.');
      }

      resetForm();
      onChanged();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được danh mục.');
    } finally {
      setIsSaving(false);
    }
  }

  async function remove(category: BlogCategory): Promise<void> {
    try {
      await deleteBlogCategory(category.id);
      message.success('Đã xóa danh mục.');

      if (editing?.id === category.id) {
        resetForm();
      }

      onChanged();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được danh mục.');
    }
  }

  const columns: TableColumnsType<BlogCategory> = [
    {
      title: 'Danh mục',
      dataIndex: 'name',
      render: (name: string, category) => (
        <div>
          <Typography.Text strong>{name}</Typography.Text>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              /blog?category={category.slug}
            </Typography.Text>
          </div>
          {category.description === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {category.description}
            </Typography.Text>
          )}
        </div>
      ),
    },
    { title: 'Số bài', dataIndex: 'postCount', width: 80, align: 'center' },
    { title: 'Thứ tự', dataIndex: 'sortOrder', width: 80, align: 'center' },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'center',
      render: (_, category) => (
        <Space size={2}>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Sửa"
              disabled={!canManage}
              onClick={() => { startEdit(category); }}
            />
          </Tooltip>
          {category.postCount > 0 ? (
            <Tooltip title={`Còn ${category.postCount.toString()} bài trong danh mục — chuyển các bài đó đi trước khi xóa.`}>
              <Button type="text" danger icon={<DeleteOutlined />} aria-label="Xóa" disabled />
            </Tooltip>
          ) : (
            <Popconfirm
              title="Xóa danh mục này?"
              okText="Xóa"
              cancelText="Không"
              okButtonProps={{ danger: true }}
              disabled={!canManage}
              onConfirm={() => { void remove(category); }}
            >
              <Tooltip title="Xóa">
                <Button type="text" danger icon={<DeleteOutlined />} aria-label="Xóa" disabled={!canManage} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      width={760}
      title="Danh mục blog"
      footer={null}
      onCancel={() => {
        resetForm();
        onClose();
      }}
    >
      {canManage ? (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ sortOrder: 0 }}
          onFinish={(values) => void submit(values)}
          style={{ background: BrandColors.surfaceSubtle, padding: '12px 12px 0', borderRadius: 8, marginBottom: 16 }}
        >
          <Row gutter={12}>
            <Col span={9}>
              <Form.Item
                name="name"
                label={editing === null ? 'Tên danh mục mới' : `Sửa: ${editing.name}`}
                rules={[{ required: true, message: 'Chưa nhập tên danh mục.' }]}
              >
                <Input placeholder="Ví dụ: Machine Vision" maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="slug" label="Đường dẫn" tooltip="Để trống thì tự sinh từ tên.">
                <Input placeholder="tự sinh từ tên" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="sortOrder" label="Thứ tự" tooltip="Số nhỏ đứng trước trên website.">
                <InputNumber<number> min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12} align="bottom">
            <Col flex="auto">
              <Form.Item name="description" label="Mô tả ngắn (tùy chọn)">
                <Input placeholder="Hiện ở đầu trang danh mục trên website" maxLength={500} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item>
                <Space>
                  {editing === null ? null : <Button onClick={resetForm}>Hủy sửa</Button>}
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSaving}
                    icon={editing === null ? <PlusOutlined /> : undefined}
                  >
                    {editing === null ? 'Thêm' : 'Lưu'}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ) : null}

      <Table<BlogCategory>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...categories]}
        pagination={false}
        locale={{ emptyText: 'Chưa có danh mục nào.' }}
      />
    </Modal>
  );
}
